/**
 * Retry utility with exponential backoff
 * Used across SDK for robust error recovery
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
const DEFAULT_OPTIONS = {
    maxAttempts: 3,
    initialDelayMs: 1000,
    maxDelayMs: 10000,
    backoffMultiplier: 2,
};
/**
 * Execute a function with retry logic and exponential backoff
 */
export function withRetry(fn_1) {
    return __awaiter(this, arguments, void 0, function* (fn, options = {}) {
        const opts = Object.assign(Object.assign({}, DEFAULT_OPTIONS), options);
        let lastError = new Error('Unknown error');
        let delay = opts.initialDelayMs;
        for (let attempt = 1; attempt <= opts.maxAttempts; attempt++) {
            try {
                return yield fn();
            }
            catch (error) {
                lastError = error instanceof Error ? error : new Error(String(error));
                if (attempt === opts.maxAttempts) {
                    break;
                }
                // Notify about retry
                if (opts.onRetry) {
                    opts.onRetry(attempt, lastError, delay);
                }
                // Wait before next attempt
                yield sleep(delay);
                // Calculate next delay with exponential backoff
                delay = Math.min(delay * opts.backoffMultiplier, opts.maxDelayMs);
            }
        }
        throw lastError;
    });
}
/**
 * Execute a function with timeout
 */
export function withTimeout(fn, timeoutMs, timeoutError) {
    return __awaiter(this, void 0, void 0, function* () {
        return new Promise((resolve, reject) => {
            const timer = setTimeout(() => {
                reject(new Error(timeoutError || `Operation timed out after ${timeoutMs}ms`));
            }, timeoutMs);
            fn()
                .then((result) => {
                clearTimeout(timer);
                resolve(result);
            })
                .catch((error) => {
                clearTimeout(timer);
                reject(error);
            });
        });
    });
}
/**
 * Execute a function with both retry and timeout
 */
export function withRetryAndTimeout(fn_1) {
    return __awaiter(this, arguments, void 0, function* (fn, retryOptions = {}, timeoutMs = 30000) {
        return withRetry(() => withTimeout(fn, timeoutMs), retryOptions);
    });
}
/**
 * Sleep utility
 */
export function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
